import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { 
  IoPersonOutline, 
  IoSearchOutline,
  IoSettingsOutline,
  IoCalendarOutline,
  IoCreateOutline,
  IoKeyOutline,
  IoMailOutline,
  IoTrashOutline,
  IoPersonAddOutline,
  IoBanOutline
} from 'react-icons/io5';
import {
  UserEditModal,
  UserCreateModal,
  UserPasswordModal,
  UserDeleteModal
} from './AdminUserModals';
import { apiErrorMessage } from '../../utils/apiError';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import RowMenu from '../../components/admin/RowMenu';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useDebouncedValue, useLatestRequest, useFirstLoad
} from '../../components/admin/useAdminTable';
import './AdminUsersPage.css';

const AdminUsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filters and search
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Expanded user details
  const [expandedUser, setExpandedUser] = useState(null);

  // Which user each modal is acting on, plus the banner shown after an action
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);
  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);
  const selection = useRowSelection(`${currentPage}|${debouncedSearch}|${sortBy}|${sortOrder}`);
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setMessage(text);
    loadUsers();
  });

  const loadUsers = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const response = await adminApi.getUsers({
        page: currentPage,
        limit: 20,
        search: debouncedSearch,
        sortBy,
        sortOrder
      });
      if (!request.isCurrent()) return;
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Load users error:', error);
      setError(apiErrorMessage(error, 'Failed to load users'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Every modal closes the same way: report what happened, then refresh so the
  // list reflects it.
  const handleActionDone = (text) => {
    setEditing(null);
    setCreating(false);
    setResettingPassword(null);
    setDeleting(null);
    setMessage(text);
    loadUsers();
  };

  const runInlineAction = async (userData, action, describe) => {
    try {
      setBusyUserId(userData.id);
      setMessage(null);
      setError(null);
      await action();
      setMessage(describe);
      loadUsers();
    } catch (err) {
      setError(apiErrorMessage(err, 'That action did not go through.'));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleToggleActive = (userData) => {
    const reactivating = userData.isActive === false;
    return runInlineAction(
      userData,
      () => adminApi.updateUser(userData.id, { isActive: reactivating }),
      `${reactivating ? 'Reactivated' : 'Deactivated'} ${userData.email}`
    );
  };

  const handleResendVerification = (userData) => runInlineAction(
    userData,
    () => adminApi.resendUserVerification(userData.id),
    `Re-sent the verification email to ${userData.email}`
  );

  const handleExpandUser = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const askUserBulk = (rows, spec) => {
    const targets = rows.filter(spec.keep);
    if (targets.length === 0) {
      setMessage(spec.empty);
      return;
    }
    bulk.ask({
      title: spec.title,
      confirmLabel: spec.confirmLabel,
      danger: spec.danger,
      body: <p className="admin-modal-hint">{spec.body} This applies to {targets.length} user{targets.length === 1 ? '' : 's'}.</p>,
      run: () => runBulk(targets, spec.run),
      summarize: (result) => summarizeBulk(spec.past, 'user', result)
    });
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading users..." />
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="admin-page-header users-page-header">
        <div>
          <h1 className="admin-page-title">User Management</h1>
          <p className="admin-page-description">
            View and manage all users, their settings, and preferences
          </p>
        </div>
        <button type="button" className="users-new-btn" onClick={() => setCreating(true)}>
          <IoPersonAddOutline /> New user
        </button>
      </div>

      {message && (
        <div className="users-message" role="status">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="admin-card">
        <div className="users-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          {/* Column headers sort on desktop; cards on narrow screens have no headers */}
          <div className="sort-controls admin-sort-mobile">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="createdAt">Date Joined</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="lastLogin">Last Login</option>
              <option value="competitiveMode">Competitive Mode</option>
              <option value="bibleTranslation">Bible Translation</option>
            </select>
            <button 
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="sort-order-btn"
            >
              {sortOrder === 'ASC' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      <AdminDataTable
        title={`Users (${pagination.totalUsers || 0})`}
        rows={users}
        getRowLabel={(userData) => userData.name || userData.email}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={loadUsers}
        emptyTitle="No users found"
        emptyMessage="No users match your search criteria."
        sort={{ key: sortBy, order: sortOrder, onChange: handleSortChange }}
        expandedId={expandedUser}
        onToggleExpand={handleExpandUser}
        renderExpanded={(userData) => (
          <div className="user-details">
            <UserDetailsView user={userData} />
          </div>
        )}
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          {
            label: 'Deactivate',
            onClick: (rows) => askUserBulk(rows, {
              title: 'Deactivate selected users',
              confirmLabel: 'Deactivate',
              past: 'Deactivated',
              keep: (row) => row.id !== user?.id && row.isActive !== false,
              empty: 'None of the selected users can be deactivated. Your own account is always left alone.',
              run: (row) => adminApi.updateUser(row.id, { isActive: false }),
              body: 'Inactive users cannot sign in. Your own account is skipped.'
            })
          },
          {
            label: 'Reactivate',
            danger: false,
            onClick: (rows) => askUserBulk(rows, {
              title: 'Reactivate selected users',
              confirmLabel: 'Reactivate',
              past: 'Reactivated',
              danger: false,
              keep: (row) => row.isActive === false,
              empty: 'None of the selected users are inactive.',
              run: (row) => adminApi.updateUser(row.id, { isActive: true }),
              body: 'These accounts will be able to sign in again.'
            })
          },
          {
            label: 'Mark verified',
            danger: false,
            onClick: (rows) => askUserBulk(rows, {
              title: 'Mark selected emails verified',
              confirmLabel: 'Mark verified',
              past: 'Verified',
              danger: false,
              keep: (row) => !row.emailVerified,
              empty: 'Every selected user is already verified.',
              run: (row) => adminApi.verifyUserEmail(row.id),
              body: 'This confirms the email without sending a message.'
            })
          }
        ]}
        columns={[
          {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (userData) => (
              <span className="admin-cell-strong">
                {userData.name}
                <span className="admin-cell-sub">
                  {userData.isAdmin && <AdminPill tone="info">Admin</AdminPill>}
                </span>
              </span>
            )
          },
          { key: 'email', header: 'Email', sortKey: 'email', render: (userData) => userData.email },
          {
            key: 'status',
            header: 'Status',
            render: (userData) => (
              <>
                {userData.isActive === false
                  ? <AdminPill tone="danger">Inactive</AdminPill>
                  : <AdminPill tone="ok">Active</AdminPill>}
                {userData.emailVerified
                  ? <AdminPill tone="ok">Verified</AdminPill>
                  : <AdminPill tone="warn">Unverified</AdminPill>}
              </>
            )
          },
          {
            key: 'competitor',
            header: 'Competitor mode',
            sortKey: 'competitiveMode',
            render: (userData) => (userData.competitiveMode
              ? <AdminPill tone="ok">On</AdminPill>
              : <AdminPill>Off</AdminPill>)
          },
          { key: 'translation', header: 'Translation', sortKey: 'bibleTranslation', render: (userData) => userData.bibleTranslation || '—' },
          { key: 'readers', header: 'Readers', render: (userData) => userData.totalReaders },
          { key: 'reports', header: 'Reports', render: (userData) => userData.totalReports },
          { key: 'joined', header: 'Joined', sortKey: 'createdAt', render: (userData) => formatDate(userData.createdAt) },
          { key: 'lastLogin', header: 'Last login', sortKey: 'lastLogin', render: (userData) => formatDate(userData.lastLogin) }
        ]}
        renderActions={(userData) => {
          const busy = busyUserId === userData.id;
          const isSelf = userData.id === user?.id;
          return (
            <>
              <button type="button" onClick={() => setEditing(userData)} disabled={busy}>
                <IoCreateOutline /> Edit
              </button>
              <Link to={`/admin/readers?userId=${userData.id}`} className="admin-row-link">
                Readers
              </Link>
              <RowMenu
                ariaLabel={`More actions for ${userData.email}`}
                items={[
                  { label: 'Set password', icon: <IoKeyOutline />, onClick: () => setResettingPassword(userData), disabled: busy },
                  {
                    label: 'Resend verification',
                    icon: <IoMailOutline />,
                    onClick: () => handleResendVerification(userData),
                    disabled: busy,
                    hidden: userData.emailVerified
                  },
                  {
                    label: userData.isActive === false ? 'Reactivate' : 'Deactivate',
                    icon: <IoBanOutline />,
                    onClick: () => handleToggleActive(userData),
                    disabled: busy || isSelf,
                    title: isSelf ? 'You cannot deactivate your own account' : undefined
                  },
                  {
                    label: 'Delete',
                    icon: <IoTrashOutline />,
                    danger: true,
                    onClick: () => setDeleting(userData),
                    disabled: busy || isSelf,
                    title: isSelf ? 'You cannot delete your own account' : undefined
                  }
                ]}
              />
            </>
          );
        }}
      />
      <BulkActionHost bulk={bulk} />

      {creating && (
        <UserCreateModal
          onClose={() => setCreating(false)}
          onSaved={handleActionDone}
        />
      )}

      {editing && (
        <UserEditModal
          user={editing}
          currentAdminId={user?.id}
          onClose={() => setEditing(null)}
          onSaved={handleActionDone}
        />
      )}

      {resettingPassword && (
        <UserPasswordModal
          user={resettingPassword}
          onClose={() => setResettingPassword(null)}
          onSaved={handleActionDone}
        />
      )}

      {deleting && (
        <UserDeleteModal
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={handleActionDone}
        />
      )}
    </div>
  );
};

const UserDetailsView = ({ user }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not set';
    return value;
  };

  return (
    <div className="user-details-content">
      <div className="details-section">
        <h4><IoPersonOutline /> Personal Information</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Full Name:</label>
            <span>{user.name}</span>
          </div>
          <div className="detail-item">
            <label>Email:</label>
            <span>{user.email}</span>
          </div>
          <div className="detail-item">
            <label>Age:</label>
            <span>{formatValue(user.age)}</span>
          </div>
          <div className="detail-item">
            <label>Gender:</label>
            <span>{formatValue(user.gender)}</span>
          </div>
          <div className="detail-item">
            <label>Account Status:</label>
            <span className={user.emailVerified ? 'status-verified' : 'status-unverified'}>
              {user.emailVerified ? 'Verified' : 'Unverified'}
            </span>
          </div>
          <div className="detail-item">
            <label>Admin Status:</label>
            <span className={user.isAdmin ? 'status-admin' : 'status-regular'}>
              {user.isAdmin ? 'Administrator' : 'Regular User'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoSettingsOutline /> Preferences & Settings</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Bible Translation:</label>
            <span>{formatValue(user.bibleTranslation)}</span>
          </div>
          <div className="detail-item">
            <label>Competitive Mode:</label>
            <span className={user.competitiveMode ? 'status-enabled' : 'status-disabled'}>
              {user.competitiveMode ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="detail-item">
            <label>App Usage Mode:</label>
            <span>{formatValue(user.appUsageMode)}</span>
          </div>
          <div className="detail-item">
            <label>Default Reading Method:</label>
            <span>{formatValue(user.defaultReadingMethod)}</span>
          </div>
          <div className="detail-item">
            <label>Default Audio Speed:</label>
            <span>{user.defaultAudioSpeed ? `${user.defaultAudioSpeed}x` : 'Not set'}</span>
          </div>
          <div className="detail-item">
            <label>Default Location:</label>
            <span>{formatValue(user.defaultLocation)}</span>
          </div>
          <div className="detail-item">
            <label>Default Time of Day:</label>
            <span>{formatValue(user.defaultTimeOfDay)}</span>
          </div>
          <div className="detail-item">
            <label>Track Reading Details:</label>
            <span className={user.trackReadingDetails ? 'status-enabled' : 'status-disabled'}>
              {user.trackReadingDetails ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoCalendarOutline /> Account Timeline</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Account Created:</label>
            <span>{formatDate(user.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Last Login:</label>
            <span>{formatDate(user.lastLogin)}</span>
          </div>
          <div className="detail-item">
            <label>Onboarding Completed:</label>
            <span className={user.onboardingCompleted ? 'status-completed' : 'status-pending'}>
              {user.onboardingCompleted ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="detail-item">
            <label>Onboarding Completed At:</label>
            <span>{formatDate(user.onboardingCompletedAt)}</span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoPersonOutline /> Readers & Activity</h4>
        <div className="readers-summary">
          <div className="summary-stat">
            <label>Total Readers:</label>
            <span className="stat-value">{user.totalReaders}</span>
          </div>
          <div className="summary-stat">
            <label>Total Reports:</label>
            <span className="stat-value">{user.totalReports}</span>
          </div>
          <div className="summary-stat">
            <label>Family Groups:</label>
            <span className="stat-value">{user.totalFamilies}</span>
          </div>
        </div>

        {user.readers && user.readers.length > 0 && (
          <div className="readers-list">
            <h5>Readers:</h5>
            {user.readers.map((reader) => (
              <div key={reader.id} className="reader-item">
                <div className="reader-info">
                  <strong>{reader.name}</strong>
                  {reader.isPrimary && <span className="primary-badge">Primary</span>}
                </div>
                <div className="reader-stats">
                  <span className="reader-stat">
                    Reports: {reader.reportCount ?? 0}
                  </span>
                  <span className={`reader-status ${reader.isActive ? 'active' : 'inactive'}`}>
                    {reader.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {user.families && user.families.length > 0 && (
          <div className="families-list">
            <h5>Family Groups:</h5>
            {user.families.map((family) => (
              <div key={family.id} className="family-item">
                <strong>{family.name}</strong>
                <span className="family-code">({family.inviteCode})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
